import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { useTexture } from "@react-three/drei";
import * as THREE from "three";
import { useScroll } from "../../scroll/ScrollProvider";

/**
 * Earth — high-quality textured globe with NASA-derived day map, bump,
 * specular ocean mask, separate cloud sphere, and a Fresnel atmospheric
 * rim. Glowing arcs connect major financial capitals (banking corridors).
 *
 * Textures are loaded from Three.js's CDN (jsdelivr) which serves the
 * canonical demo planet textures the WebGL community has used for years.
 */

const TEX = {
  // Higher-resolution earth assets from a reliable CDN-hosted demo set
  day: "https://threejs.org/examples/textures/planets/earth_atmos_2048.jpg",
  bump: "https://threejs.org/examples/textures/planets/earth_normal_2048.jpg",
  specular: "https://threejs.org/examples/textures/planets/earth_specular_2048.jpg",
  clouds: "https://threejs.org/examples/textures/planets/earth_clouds_1024.png",
} as const;

const CAPITALS: { name: string; lat: number; lon: number }[] = [
  { name: "New York", lat: 40.7, lon: -74.0 },
  { name: "London", lat: 51.5, lon: -0.13 },
  { name: "Zurich", lat: 47.4, lon: 8.55 },
  { name: "Frankfurt", lat: 50.1, lon: 8.7 },
  { name: "Dubai", lat: 25.2, lon: 55.3 },
  { name: "Singapore", lat: 1.35, lon: 103.8 },
  { name: "Hong Kong", lat: 22.3, lon: 114.2 },
  { name: "Tokyo", lat: 35.7, lon: 139.7 },
  { name: "Geneva", lat: 46.2, lon: 6.14 },
  { name: "Vaduz", lat: 47.14, lon: 9.52 },
  { name: "Luxembourg", lat: 49.61, lon: 6.13 },
];

const ARC_PAIRS: [number, number][] = [
  [0, 1], [1, 2], [2, 4], [4, 5], [5, 6], [6, 7], [7, 0],
  [1, 3], [3, 9], [2, 8], [3, 10],
];

const RADIUS = 1.55;
const CLOUD_RADIUS = 1.582;

function latLonToVec3(lat: number, lon: number, r: number): THREE.Vector3 {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta),
  );
}

function buildArc(a: THREE.Vector3, b: THREE.Vector3): THREE.BufferGeometry {
  const mid = a.clone().add(b).multiplyScalar(0.5);
  const dist = a.distanceTo(b);
  const lift = 0.32 + Math.min(0.6, dist * 0.42);
  mid.normalize().multiplyScalar(RADIUS + lift);
  const curve = new THREE.QuadraticBezierCurve3(a, mid, b);
  return new THREE.BufferGeometry().setFromPoints(curve.getPoints(40));
}

export function Earth() {
  const root = useRef<THREE.Group>(null);
  const sphere = useRef<THREE.Mesh>(null);
  const clouds = useRef<THREE.Mesh>(null);
  const arcs = useRef<THREE.Group>(null);

  const [dayMap, bumpMap, specMap, cloudsMap] = useTexture([
    TEX.day,
    TEX.bump,
    TEX.specular,
    TEX.clouds,
  ]);

  // Tune textures
  useMemo(() => {
    [dayMap, bumpMap, specMap, cloudsMap].forEach((t) => {
      t.anisotropy = 16;
    });
    dayMap.colorSpace = THREE.SRGBColorSpace;
    cloudsMap.colorSpace = THREE.SRGBColorSpace;
  }, [dayMap, bumpMap, specMap, cloudsMap]);

  const arcGeoms = useMemo(
    () =>
      ARC_PAIRS.map(([i, j]) =>
        buildArc(
          latLonToVec3(CAPITALS[i].lat, CAPITALS[i].lon, RADIUS + 0.005),
          latLonToVec3(CAPITALS[j].lat, CAPITALS[j].lon, RADIUS + 0.005),
        ),
      ),
    [],
  );

  const capitalPositions = useMemo(
    () => CAPITALS.map((c) => latLonToVec3(c.lat, c.lon, RADIUS + 0.005)),
    [],
  );

  const { section, sectionProgress } = useScroll();

  useFrame((_state, delta) => {
    if (root.current) root.current.rotation.z = THREE.MathUtils.degToRad(23.4);
    if (sphere.current) sphere.current.rotation.y += delta * 0.05;
    if (clouds.current) clouds.current.rotation.y += delta * 0.02;
    if (arcs.current) {
      const active = section < 4 ? 0 : section === 4 ? sectionProgress : 1;
      arcs.current.children.forEach((line) => {
        const m = (line as THREE.Line).material as THREE.LineBasicMaterial;
        m.opacity = 0.22 + active * 0.7;
      });
    }
  });

  return (
    <group ref={root}>
      <mesh ref={sphere} castShadow receiveShadow>
        <sphereGeometry args={[RADIUS, 72, 48]} />
        <meshPhongMaterial
          map={dayMap}
          bumpMap={bumpMap}
          bumpScale={0.04}
          specularMap={specMap}
          specular={new THREE.Color("#3b6db0")}
          shininess={18}
          emissive="#0a1426"
          emissiveIntensity={0.08}
        />
      </mesh>

      <mesh ref={clouds}>
        <sphereGeometry args={[CLOUD_RADIUS, 56, 40]} />
        <meshPhongMaterial
          map={cloudsMap}
          transparent
          opacity={0.6}
          depthWrite={false}
        />
      </mesh>

      <group ref={arcs}>
        {arcGeoms.map((g, i) => (
          <line key={i}>
            <primitive object={g} attach="geometry" />
            <lineBasicMaterial
              color="#f6d28a"
              transparent
              opacity={0.55}
              depthWrite={false}
              toneMapped={false}
            />
          </line>
        ))}
      </group>

      {capitalPositions.map((p, i) => (
        <mesh key={i} position={p}>
          <sphereGeometry args={[0.025, 16, 16]} />
          <meshStandardMaterial
            color="#fff5dc"
            emissive="#ffb14a"
            emissiveIntensity={2.6}
            toneMapped={false}
          />
        </mesh>
      ))}

      <pointLight position={[3, 2, 4]} intensity={1.6} color="#fff1d4" distance={12} decay={2} />
    </group>
  );
}

useTexture.preload(TEX.day);
useTexture.preload(TEX.bump);
useTexture.preload(TEX.specular);
useTexture.preload(TEX.clouds);
